import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import {
  Container,
  TextInput,
  Box,
  Stack,
  Text,
  Title,
  Flex,
  Button,
  Paper,
  Tabs,
  rem,
  useMantineTheme,
} from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { IconSearch, IconSparkles, IconBuilding, IconMapPin, IconArrowLeft } from "@tabler/icons-react";
import { useLocation, useSearchParams } from "react-router-dom";

import { type CompanyWithUI } from "../../components/shared/CompanyCardHorizontal";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
import { useAuth } from "@/hooks/useAuth";
import PaginationSignupModal from "@/components/shared/UserSignupModal";
import DiscoverPromoCarousel, { type DiscoverPromoSlide } from "./components/DiscoverPromoCarousel";
import { DiscoverPickCombobox } from "./components/DiscoverPickCombobox";
import { OperatingIndustry } from "@gt/prisma";
import { getCountriesByRegion, getIndustryOptions, getRegionOptionsForSelect } from "../../components/shared/constants/general.const";
import {
  DEFAULT_DISCOVER_PROMO_SLIDES,
  DEFAULT_PAGE_SIZE,
  DISCOVER_PAGE_SIZE_OPTIONS,
  DISCOVER_TYPEWRITER_PHRASES,
  LOADER_MIN_VISIBLE_MS,
  LOADER_SHOW_DELAY_MS,
  MAX_RETRIES,
  QUICK_SEARCH_DEBOUNCE_MS,
  MOBILE_PROMO_STRIP_PX,
  MOBILE_PROMO_STRIP_RESERVE,
} from "./DiscoverPage.constants";
import { readGuestShortlistCompanyIds } from "@/shared/utils/guestShortlistStorage";
import { SectorCardsGrid } from "@/pages/home/SectorCardsGrid";
import AppHeaderUserActions from "@/components/shared/AppHeaderUserActions";
import { getSectorBySlug, type SectorCard } from "@/pages/home/sectorCatalog";
import { useUser } from "@/context/UserContext";
import { useTypewriterPlaceholder } from "./hooks/useTypewriterPlaceholder";
import { useSmoothLoading } from "./hooks/useSmoothLoading";
import { deriveSearchByFromQuery } from "./utils/discoverSearch";
import { extractAiSearchResults, mapAiResultsToCompanies, mapListCompanies, mapQuickSearchCompanies } from "./utils/discoverMappers";
import { DiscoverResultsSection } from "./components/DiscoverResultsSection";
import { useVerificationModalOpener } from "@/components/layout/AppLayout";
// import FilterChips from "./FilterChips.tsx";

type SearchMode = "browse" | "ai";

export type DiscoverPageProps = {
  /** When set and non-empty, replaces the default promo carousel data. UI stays in `DiscoverPromoCarousel`. */
  promoCarouselSlides?: DiscoverPromoSlide[];
  /** Optional initial keyword for pre-filtered experiences (e.g. sector landing). */
  initialKeyword?: string;
};

export default function DiscoverPage({ promoCarouselSlides, initialKeyword }: DiscoverPageProps) {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { addToShortlist, removeFromShortlist, userDetails } = useUser();
  const openVerificationModal = useVerificationModalOpener();
  const theme = useMantineTheme();
  const isLgUp = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`, false, { getInitialValueInEffect: true });
  const isSmUp = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, false, { getInitialValueInEffect: true });
  const isHomeRoute = location.pathname === "/";
  const isMyDiscoveryRoute = location.pathname.startsWith("/my-discovery");
  const shouldShowPromoCarousel = !isAuthenticated;

  /** Mobile: when true, promo sits in-flow after pagination; when false, fixed strip at viewport bottom. */
  const [mobileDocked, setMobileDocked] = useState(false);
  const mobileDockSentinelRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  /** Browse `quickSearch` and AI tab requests use this so we do not hit the API on every keystroke. */
  const [debouncedSearch] = useDebouncedValue(searchQuery, QUICK_SEARCH_DEBOUNCE_MS);
  const latestAiQueryRef = useRef<string>("");
  const latestQuickSearchKeyRef = useRef<string>("");
  const inFlightAiQueryRef = useRef<string | null>(null);
  const inFlightListAllKeyRef = useRef<string | null>(null);
  const inFlightQuickSearchKeyRef = useRef<string | null>(null);
  const [searchMode, setSearchMode] = useState<SearchMode>("browse");

  const derivedSearchBy = useMemo(() => deriveSearchByFromQuery(searchQuery), [searchQuery]);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyWithUI[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  /** Landing (`/`) always uses 10 per page; `/discover` can use the selectable page size. */
  const effectivePageSize = isHomeRoute ? DEFAULT_PAGE_SIZE : pageSize;
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isAiSearch, setIsAiSearch] = useState(false);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [paginationSignupModalOpened, setPaginationSignupModalOpened] = useState(false);
  const [guestShortlistedIds, setGuestShortlistedIds] = useState<Set<string>>(new Set());
  const hasHydratedFromUrlRef = useRef(false);
  const skipNextUrlSyncRef = useRef(false);

  const [selectedSectorTitle, setSelectedSectorTitle] = useState<string | null>(null);
  /** Set when the user picks a sector card (sidebar or grid); used in URL so back navigation restores layout. */
  const [selectedSectorSlug, setSelectedSectorSlug] = useState<string | null>(null);

  /** Same layout as clicking a sector card: left column sector filters whenever quick search has criteria (keyword, region, or industry). */
  const hasActiveQuickSearch = useMemo(
    () =>
      Boolean(
        searchQuery.trim() ||
        debouncedSearch.trim() ||
        selectedRegion ||
        selectedIndustry,
      ),
    [searchQuery, debouncedSearch, selectedRegion, selectedIndustry],
  );
  const sectorsInSidebar = searchMode === "browse" && hasActiveQuickSearch;
  const typewriterPlaceholder = useTypewriterPlaceholder({
    phrases: DISCOVER_TYPEWRITER_PHRASES,
    inputValue: searchQuery,
  });
  const smoothLoading = useSmoothLoading({
    loading,
    showDelayMs: LOADER_SHOW_DELAY_MS,
    minVisibleMs: LOADER_MIN_VISIBLE_MS,
  });
  const companyStatus = userDetails?.company?.status;
  const shouldForceVerification =
    isAuthenticated &&
    isMyDiscoveryRoute &&
    companyStatus !== "VERIFIED" &&
    companyStatus !== "SYSTEM_VERIFIED" &&
    companyStatus !== "UNDER_REVIEW";
  const canApplyForVerification = shouldForceVerification;

  // Modal auto-opening is handled globally by AppLayout.

  /** Hydrate filters from URL (browser back/forward, shared links). Layout phase avoids sync effect clearing params before state is applied. */
  useLayoutEffect(() => {
    skipNextUrlSyncRef.current = true;
    const params = new URLSearchParams(location.search);
    const urlKeyword = params.get("keyword")?.trim() ?? "";
    const urlIndustry = params.get("industry")?.trim() || null;
    const urlRegion = params.get("region")?.trim() || null;
    const urlSectorSlug = params.get("sector")?.trim() || null;
    const urlMode = params.get("mode") === "ai" ? "ai" : "browse";
    const urlPage = params.get("page");
    const isSectorRoute = location.pathname.startsWith("/sector/");
    const hasUrlDiscoverState = Boolean(
      urlKeyword || urlIndustry || urlRegion || urlSectorSlug || urlMode === "ai",
    );

    if (isSectorRoute && initialKeyword?.trim() && !hasUrlDiscoverState) {
      setSearchMode("browse");
      setSearchQuery(initialKeyword.trim());
      setSelectedIndustry(null);
      setSelectedRegion(null);
      setSelectedSectorTitle(null);
      setSelectedSectorSlug(null);
      setPage(1);
      hasHydratedFromUrlRef.current = true;
      return;
    }

    setSearchMode(urlMode);
    setSearchQuery(urlKeyword);
    setSelectedRegion(urlRegion);

    if (urlSectorSlug) {
      const sector = getSectorBySlug(urlSectorSlug);
      setSelectedSectorSlug(urlSectorSlug);
      setSelectedSectorTitle(sector?.title ?? null);
      setSelectedIndustry(urlIndustry ?? sector?.industryCategory ?? null);
    } else {
      setSelectedSectorSlug(null);
      setSelectedSectorTitle(null);
      setSelectedIndustry(urlIndustry);
    }

    if (urlPage && !isHomeRoute) {
      const p = parseInt(urlPage, 10);
      if (!Number.isNaN(p)) setPage(Math.max(1, p));
    } else if (!isSectorRoute || hasUrlDiscoverState) {
      if (!urlPage) setPage(1);
    }
    hasHydratedFromUrlRef.current = true;
  }, [location.search, location.pathname, initialKeyword, isHomeRoute]);

  /** Keep discover filters in the URL so returning from a company profile restores them. */
  useEffect(() => {
    if (!hasHydratedFromUrlRef.current) return;
    if (skipNextUrlSyncRef.current) {
      skipNextUrlSyncRef.current = false;
      return;
    }

    const params = new URLSearchParams();
    if (searchMode === "ai") params.set("mode", "ai");
    const q = searchQuery.trim();
    if (q) params.set("keyword", q);
    if (selectedIndustry) params.set("industry", selectedIndustry);
    if (selectedRegion) params.set("region", selectedRegion);
    if (selectedSectorSlug) params.set("sector", selectedSectorSlug);
    if (page > 1 && !isHomeRoute) params.set("page", String(page));

    const nextStr = params.toString();
    const cur = new URLSearchParams(location.search).toString();
    if (nextStr !== cur) {
      setSearchParams(params, { replace: true });
    }
  }, [
    searchQuery,
    selectedIndustry,
    selectedRegion,
    selectedSectorSlug,
    searchMode,
    page,
    isHomeRoute,
    location.search,
    setSearchParams,
  ]);

  useEffect(() => {
    if (isHomeRoute) setPage(1);
  }, [isHomeRoute]);

  useEffect(() => {
    if (!isHomeRoute || isAuthenticated) {
      setGuestShortlistedIds(new Set());
      return;
    }
    setGuestShortlistedIds(new Set(readGuestShortlistCompanyIds()));
  }, [isHomeRoute, isAuthenticated]);

  useEffect(() => {
    if (!isHomeRoute || isAuthenticated) return;
    setCompanies((prev) =>
      prev.map((company) => ({
        ...company,
        isShortlisted: guestShortlistedIds.has(company.id),
      })),
    );
  }, [guestShortlistedIds, isHomeRoute, isAuthenticated]);

  const discoverPromoSlides =
    promoCarouselSlides && promoCarouselSlides.length > 0 ? promoCarouselSlides : DEFAULT_DISCOVER_PROMO_SLIDES;

  const discoverRegionOptions = useMemo(
    () => [...getRegionOptionsForSelect()].sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );

  const discoverIndustryOptions = useMemo(
    () => [...getIndustryOptions()].sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );

  // Accepts explicit page to avoid stale closure when called after page reset
  const fetchCompanies = async (pageToFetch = page) => {
    if (retryCount >= MAX_RETRIES) {
      setLoading(false);
      return;
    }

    try {
      const pageForRequest = isAuthenticated ? pageToFetch : 1;
      const useDiscoverEndpoint = isAuthenticated && isMyDiscoveryRoute;
      const requestKey = JSON.stringify({
        kind: useDiscoverEndpoint ? "discover" : "listAll",
        page: pageForRequest,
        limit: effectivePageSize,
      });
      if (inFlightListAllKeyRef.current === requestKey) return;
      inFlightListAllKeyRef.current = requestKey;

      setLoading(true);
      setLoadError(null);
      setMatchCount(null);

      const response = useDiscoverEndpoint
        ? await trpc.company.discover.query({
          page: pageForRequest,
          limit: effectivePageSize,
        })
        : await trpc.company.listAll.query({
          page: pageForRequest,
          limit: effectivePageSize,
        });

      const mappedCompanies = mapListCompanies(response.companies || [], isAuthenticated, guestShortlistedIds);

      setCompanies(mappedCompanies);
      setTotalPages(response.pagination?.totalPages || 1);
      setMatchCount(response.pagination?.total ?? mappedCompanies.length);
      setRetryCount(0);
    } catch (err) {
      handleTrpcError(err);
      const nextRetry = retryCount + 1;
      setRetryCount(nextRetry);
      if (nextRetry >= MAX_RETRIES) {
        setLoadError("Unable to load companies. Please try again later.");
      }
    } finally {
      inFlightListAllKeyRef.current = null;
      setLoading(false);
    }
  };

  const fetchAiSearch = async (query: string) => {
    if (inFlightAiQueryRef.current === query) return;
    inFlightAiQueryRef.current = query;
    latestAiQueryRef.current = query;
    setLoading(true);
    setLoadError(null);
    setIsAiSearch(true);
    setMatchCount(null);

    try {
      const response = await trpc.company.searchAI.query({ query });

      if (latestAiQueryRef.current !== query) return;

      const results = extractAiSearchResults(response);
      const mappedCompanies = mapAiResultsToCompanies(results, guestShortlistedIds);

      setCompanies(mappedCompanies);
      setTotalPages(1);
      setMatchCount(mappedCompanies.length);
    } catch (err) {
      if (latestAiQueryRef.current !== query) return;
      handleTrpcError(err);
      setLoadError("AI search failed. Please try again.");
    } finally {
      if (inFlightAiQueryRef.current === query) {
        inFlightAiQueryRef.current = null;
      }
      if (latestAiQueryRef.current === query) {
        setLoading(false);
      }
    }
  };

  const fetchQuickSearch = async (
    query: string,
    pageToFetch = page,
    filters: { region: string | null; industry: string | null } = {
      region: selectedRegion,
      industry: selectedIndustry,
    },
  ) => {
    const effectiveSearchBy = deriveSearchByFromQuery(query);
    const effectiveQuery = effectiveSearchBy === "gtid" ? query.trim().toUpperCase() : query.trim();
    const requestKey = JSON.stringify({
      q: effectiveQuery,
      r: filters.region,
      i: filters.industry,
      p: pageToFetch,
      l: effectivePageSize,
      sb: effectiveSearchBy,
    });
    if (inFlightQuickSearchKeyRef.current === requestKey) return;
    inFlightQuickSearchKeyRef.current = requestKey;
    latestQuickSearchKeyRef.current = requestKey;
    setLoading(true);
    setLoadError(null);
    setIsAiSearch(false);
    setMatchCount(null);

    try {
      const response = await trpc.company.quickSearch.query({
        query: effectiveQuery,
        page: pageToFetch,
        limit: effectivePageSize,
        ...(filters.region ? { region: filters.region } : {}),
        ...(filters.industry ? { category: filters.industry as OperatingIndustry } : {}),
        searchBy: effectiveSearchBy,
      });

      if (latestQuickSearchKeyRef.current !== requestKey) return;

      const mappedCompanies = mapQuickSearchCompanies(response.companies || [], guestShortlistedIds);

      setCompanies(mappedCompanies);
      setTotalPages(response.pagination?.totalPages || 1);
      setMatchCount(response.pagination?.total ?? mappedCompanies.length);
    } catch (err) {
      if (latestQuickSearchKeyRef.current !== requestKey) return;
      handleTrpcError(err);
      setLoadError("Quick search failed. Please try again.");
    } finally {
      if (inFlightQuickSearchKeyRef.current === requestKey) {
        inFlightQuickSearchKeyRef.current = null;
      }
      if (latestQuickSearchKeyRef.current === requestKey) {
        setLoading(false);
      }
    }
  };

  // Triggers on search query change. Resets page and dispatches the right fetch.
  useEffect(() => {
    // On back navigation with URL filters, wait for URL -> state hydration first.
    // This prevents an initial unfiltered listAll call before filters are restored.
    if (!hasHydratedFromUrlRef.current && location.search) return;

    setRetryCount(0);
    setLoadError(null);

    const rawQuery = searchQuery.trim();
    const q = debouncedSearch.trim();
    const hasQuickCriteria = Boolean(q || selectedRegion || selectedIndustry);
    // Prevent a transient "load all" fetch while URL/state hydration restores the query
    // but the debounced value has not caught up yet.
    if (rawQuery && rawQuery !== q) return;

    if (searchMode === "ai") {
      setPage(1);
      if (!q) {
        setIsAiSearch(false);
        void fetchCompanies(1);
        return;
      }
      void fetchAiSearch(q);
      return;
    }

    if (!hasQuickCriteria) {
      setIsAiSearch(false);
      setPage(1);
      void fetchCompanies(1);
      return;
    }

    setPage(1);
    void fetchQuickSearch(q, 1, { region: selectedRegion, industry: selectedIndustry });
  }, [searchQuery, debouncedSearch, searchMode, selectedRegion, selectedIndustry, effectivePageSize, isHomeRoute]);

  const handleRunSearch = () => {
    const query = searchQuery.trim();
    setPage(1);
    setRetryCount(0);
    setLoadError(null);

    if (searchMode === "ai") {
      if (!query) {
        setIsAiSearch(false);
        void fetchCompanies(1);
        return;
      }
      void fetchAiSearch(query);
      return;
    }

    const hasQuick = Boolean(query || selectedRegion || selectedIndustry);
    if (!hasQuick) {
      setIsAiSearch(false);
      void fetchCompanies(1);
      return;
    }

    void fetchQuickSearch(query, 1, { region: selectedRegion, industry: selectedIndustry });
  };

  const handlePaginationChange = (nextPage: number) => {
    if (isAiSearch) return;
    if (!isAuthenticated && nextPage > 1) {
      setPaginationSignupModalOpened(true);
      return;
    }
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
    const query = debouncedSearch.trim();
    const hasQuick = Boolean(query || selectedRegion || selectedIndustry);
    if (hasQuick) {
      void fetchQuickSearch(query, nextPage, { region: selectedRegion, industry: selectedIndustry });
      return;
    }
    void fetchCompanies(nextPage);
  };

  const handlePageSizeChange = (next: number) => {
    setPage(1);
    setPageSize(next);
  };

  const updateMobileDocked = useCallback(() => {
    if (isLgUp) return;
    const el = mobileDockSentinelRef.current;
    if (!el) {
      setMobileDocked(true);
      return;
    }
    const top = el.getBoundingClientRect().top;
    setMobileDocked(top <= window.innerHeight - MOBILE_PROMO_STRIP_PX + 2);
  }, [isLgUp]);


  const buildDiscoverProfileSearch = useCallback(() => {
    const params = new URLSearchParams();
    if (searchMode === "ai") params.set("mode", "ai");
    const q = searchQuery.trim();
    if (q) params.set("keyword", q);
    if (selectedIndustry) params.set("industry", selectedIndustry);
    if (selectedRegion) params.set("region", selectedRegion);
    if (selectedSectorSlug) params.set("sector", selectedSectorSlug);
    if (page > 1 && !isHomeRoute) params.set("page", String(page));
    const s = params.toString();
    return s ? `?${s}` : "";
  }, [searchMode, searchQuery, selectedIndustry, selectedRegion, selectedSectorSlug, page, isHomeRoute]);

  const handlePhraseClick = (phrase: string) => {
    setSearchMode("browse");
    setSearchQuery(phrase);
    setPage(1);
    setSelectedSectorTitle(null);
    setSelectedSectorSlug(null);
  };

  const handleSectorSelect = (sector: SectorCard) => {
    // Clear current list immediately so old sector cards never remain visible
    // while the next sector search is loading.
    setCompanies([]);
    setMatchCount(null);
    setTotalPages(1);
    setLoadError(null);

    setSearchMode("browse");
    setSearchQuery("");
    setSelectedRegion(null);
    setSelectedIndustry(sector.industryCategory);
    setPage(1);
    setSelectedSectorTitle(sector.title);
    setSelectedSectorSlug(sector.slug);
    void fetchQuickSearch("", 1, {
      region: null,
      industry: sector.industryCategory as OperatingIndustry,
    });
  };


  useLayoutEffect(() => {
    if (isLgUp) return undefined;
    updateMobileDocked();
    window.addEventListener("scroll", updateMobileDocked, { passive: true });
    window.addEventListener("resize", updateMobileDocked);
    return () => {
      window.removeEventListener("scroll", updateMobileDocked);
      window.removeEventListener("resize", updateMobileDocked);
    };
  }, [
    isLgUp,
    updateMobileDocked,
    companies.length,
    page,
    effectivePageSize,
    totalPages,
    loading,
    isAiSearch,
    loadError,
    debouncedSearch,
  ]);

  const mobilePromoBar = (
    <Box px={0} style={{ pointerEvents: "auto" }}>
      <Paper
        radius={0}
        withBorder={false}
        shadow="none"
        bg="transparent"
        p={5}
        w="100%"
        style={{
          overflow: "hidden",
        }}
      >
        <DiscoverPromoCarousel slides={discoverPromoSlides} height={70} autoplayDelayMs={4500} />
      </Paper>
    </Box>
  );

  const effectiveLoading = searchMode === "ai" ? loading : smoothLoading;
  const showResultsShimmer = effectiveLoading && companies.length === 0;

  const discoverMainBody = (
    <DiscoverResultsSection
      companies={companies}
      selectedSectorTitle={selectedSectorTitle}
      debouncedSearch={debouncedSearch}
      isAuthenticated={isAuthenticated}
      isAiSearch={isAiSearch}
      loading={loading}
      effectiveLoading={effectiveLoading}
      showResultsShimmer={showResultsShimmer}
      matchCount={matchCount}
      loadError={loadError}
      isHomeRoute={isHomeRoute}
      totalPages={totalPages}
      page={page}
      effectivePageSize={effectivePageSize}
      pageSizeOptions={isHomeRoute ? [DEFAULT_PAGE_SIZE] : DISCOVER_PAGE_SIZE_OPTIONS}
      onPageChange={handlePaginationChange}
      onPageSizeChange={isHomeRoute ? undefined : handlePageSizeChange}
      addToShortlist={addToShortlist}
      removeFromShortlist={removeFromShortlist}
      setCompanies={setCompanies}
      setGuestShortlistedIds={setGuestShortlistedIds}
      buildDiscoverProfileSearch={buildDiscoverProfileSearch}
      isMyDiscoveryRoute={isMyDiscoveryRoute}
      canViewCompanyDetails={!shouldForceVerification}
      onBlockedViewCompanyDetails={openVerificationModal}
    />
  );

  return (
    <Box
      pb={{
        base: shouldShowPromoCarousel && !isLgUp && !mobileDocked ? MOBILE_PROMO_STRIP_RESERVE : rem(8),
        lg: rem(40),
      }}
    >
      {/* Logged-in Explore Companies header (Dashboard-style) */}
      {isMyDiscoveryRoute ? (
        <Box py="md" style={{ borderBottom: "1px solid #E5E7EB" }}>
          <Flex
            justify="space-between"
            pl={20}
            pr={20}
            align={{ base: "flex-start", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            gap="sm"
          >
            <Box>
              <Title order={2} fw={400} fz={{ base: 20, sm: 24 }}>
                Explore your companies
              </Title>
              <Text
                c="dimmed"
                mt={4}
                size="xs"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Explore verified companies with advanced search criteria.
              </Text>
            </Box>
            <Flex align="center" gap="sm">
              {canApplyForVerification ? (
                <Button bg="green" size="xs" onClick={openVerificationModal}>
                  Apply for verification
                </Button>
              ) : null}
              <AppHeaderUserActions />
            </Flex>
          </Flex>
        </Box>
      ) : null}

      {/* Mobile Header (kept for non-home Discover variants) */}
      {!isHomeRoute && !isMyDiscoveryRoute ? (
        <Box py="md" hiddenFrom="sm">
          <Container size={1414}>
            <Title order={2} fz={20} fw={600}>
              Explore Companies
            </Title>
            <Text size="sm" c="slate.7">
              Explore verified companies with advanced search criteria.
            </Text>
          </Container>
        </Box>
      ) : null}

      <Container size={1414} py={{ base: isMyDiscoveryRoute ? "md" : 0, sm: 40 }}>
        <Stack gap="md" mb="lg">
          <Title order={2} fz={{ base: 24, sm: 28, md: 32 }} fw={700} lh={1.15} maw={760}>
            Trusted Companies. Better Partnerships.
          </Title>

          <Tabs value={searchMode} onChange={(v) => setSearchMode((v as SearchMode) || "browse")} variant="unstyled">
            <Tabs.List
              style={{
                width: "100%",
                maxWidth: "min(640px, 100%)",
                border: "1px solid #04305c",
                background: "#e5e7eb",
                borderRadius: 999,
                padding: 3,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                overflow: "hidden",
              }}
            >
              <Tabs.Tab
                value="browse"
                style={{
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 999,
                  backgroundColor: searchMode === "browse" ? "#04305c" : "transparent",
                  color: searchMode === "browse" ? "#ffffff" : "#04305c",
                  fontWeight: 700,
                  fontSize: "clamp(12px, 2.5vw, 16px)",
                  lineHeight: 1.2,
                  textAlign: "center",
                  padding: "clamp(8px, 1.6vw, 10px) clamp(6px, 1.8vw, 12px)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Keyword Search
              </Tabs.Tab>
              <Tabs.Tab
                value="ai"
                leftSection={<IconSparkles size={14} />}
                style={{
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 999,
                  backgroundColor: searchMode === "ai" ? "#04305c" : "transparent",
                  color: searchMode === "ai" ? "#ffffff" : "#04305c",
                  fontWeight: 700,
                  fontSize: "clamp(12px, 2.5vw, 16px)",
                  lineHeight: 1.2,
                  textAlign: "center",
                  padding: "clamp(8px, 1.6vw, 10px) clamp(6px, 1.8vw, 12px)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                AI-Powered Matching
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>

          {searchMode === "browse" ? (
            <Flex
              direction={{ base: "column", sm: "row" }}
              align={{ base: "stretch", sm: "flex-end" }}
              gap={{ base: rem(6), sm: "sm" }}
              wrap="nowrap"
              w="100%"
            >
              <Box style={{ flex: isSmUp ? "1 1 0" : "0 0 auto", minWidth: 0, width: "100%" }}>
                <TextInput
                  placeholder={derivedSearchBy === "gtid" ? "Enter GTID" : typewriterPlaceholder || "What do you need help with?"}
                  leftSection={<IconSearch size={18} />}
                  size="md"
                  radius="md"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  style={{
                    minWidth: 0,
                    width: "100%",
                  }}
                />
              </Box>
              <Flex
                direction="row"
                align="flex-end"
                gap={{ base: rem(6), sm: "sm" }}
                wrap="nowrap"
                w={{ base: "100%", sm: rem(360) }}
                miw={0}
                style={{ flex: isSmUp ? "0 0 auto" : "0 0 auto" }}
              >
                <Box style={{ flex: "1 1 0", minWidth: 0 }}>
                  <DiscoverPickCombobox
                    placeholder="Pick industry"
                    leftSection={<IconBuilding size={18} color="#04305c" />}
                    value={selectedIndustry}
                    onChange={(v) => {
                      setSelectedSectorSlug(null);
                      setSelectedIndustry(v);
                    }}
                    options={discoverIndustryOptions}
                  />
                </Box>
                <Box style={{ flex: "1 1 0", minWidth: 0 }}>
                  <DiscoverPickCombobox
                    placeholder="Pick region"
                    leftSection={<IconMapPin size={18} color="#04305c" />}
                    value={selectedRegion}
                    onChange={(v) => {
                      setSelectedSectorSlug(null);
                      setSelectedRegion(v);
                    }}
                    options={discoverRegionOptions}
                    getOptionDetail={(regionId) => {
                      const countries = getCountriesByRegion(regionId);
                      if (countries.length === 0) return null;
                      return `${countries.length} countries: ${countries.join(", ")}`;
                    }}
                  />
                </Box>
              </Flex>
              <Button
                color="#04305c"
                radius="md"
                size="md"
                px="xl"
                onClick={handleRunSearch}
                fullWidth={!isSmUp}
                style={{ flex: "0 0 auto", minWidth: isSmUp ? 170 : "100%" }}
                leftSection={<IconSearch size={18} />}
              >
                Search
              </Button>
            </Flex>
          ) : (
            <Flex
              direction={{ base: "column", sm: "row" }}
              align={{ base: "stretch", sm: "flex-end" }}
              gap={{ base: rem(6), sm: "sm" }}
            >
              <TextInput
                placeholder={typewriterPlaceholder || "What do you need help with?"}
                leftSection={<IconSearch size={18} />}
                size="md"
                radius="md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                style={{ flex: isSmUp ? 1 : "0 0 auto", minWidth: 0, width: "100%" }}
              />
              <Button
                color="#04305c"
                radius="md"
                size="md"
                px="xl"
                onClick={handleRunSearch}
                fullWidth={!isLgUp}
                style={{ flex: "0 0 auto", minWidth: isLgUp ? 170 : "100%" }}
                leftSection={<IconSearch size={18} />}
              >
                Search
              </Button>
            </Flex>
          )}

          {searchMode === "browse" && derivedSearchBy === "keyword" && isSmUp ? (
            <Flex gap="sm" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
              <Text size="xs" c="slate.6" style={{ flexShrink: 0 }}>
                Try quick search examples:
              </Text>
              <Flex
                gap="xs"
                wrap="nowrap"
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflowX: "auto",
                  paddingBottom: 2,
                }}
              >
                {DISCOVER_TYPEWRITER_PHRASES.map((phrase) => (
                  <Button
                    key={phrase}
                    size="xs"
                    radius="xl"
                    variant="light"
                    color="blue"
                    style={{ flexShrink: 0 }}
                    onClick={() => handlePhraseClick(phrase)}
                  >
                    {phrase}
                  </Button>
                ))}
              </Flex>
            </Flex>
          ) : null}

        </Stack>

        {searchMode === "browse" && !sectorsInSidebar && isSmUp ? (
          <Box mt="lg" mb="lg">
            <SectorCardsGrid onSectorSelect={handleSectorSelect} />
          </Box>
        ) : null}

        {searchMode === "browse" && sectorsInSidebar && isSmUp ? (
          <Flex
            gap="md"
            align="flex-start"
            direction={{ base: "column-reverse", md: "row" }}
            mb="lg"
            mt="md"
          >
            <Box
              w={{ base: "100%", md: 300 }}
              maw={{ md: 300 }}
              style={{ flexShrink: 0, alignSelf: "flex-start" }}
              pos={{ md: "sticky" }}
              top={{ md: rem(96) }}

            >
              <Button
                variant="filled"
                color="primary"
                size="xs"
                fullWidth
                mb="sm"
                leftSection={<IconArrowLeft size={14} stroke={1.75} />}
                onClick={() => {
                  setSelectedSectorTitle(null);
                  setSelectedSectorSlug(null);
                  setSelectedIndustry(null);
                  setSelectedRegion(null);
                  setSearchQuery("");
                }}
              >
                Back
              </Button>
              <Box
                style={{
                  height: "calc(100vh - 240px)",
                  maxHeight: "calc(100vh - 240px)",
                  minHeight: 360,
                  overflowY: "auto",
                  paddingRight: 4,
                }}
              >
                <SectorCardsGrid variant="sidebar" onSectorSelect={handleSectorSelect} />
              </Box>
            </Box>
            <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
              {discoverMainBody}
            </Stack>
          </Flex>
        ) : (
          discoverMainBody
        )}

        {/* Filters hidden — uncomment to restore
        <Group gap="md" align="flex-end" visibleFrom="sm">
          <MultiSelectWithBadge placeholder="Industry" data={industryOptions} value={selectedIndustries} onChange={setSelectedIndustries} leftSection={<IconBuilding size={18} />} width={148} searchable clearable />
          <MultiSelectWithBadge placeholder="Country" data={countryOptions} value={selectedCountries} onChange={setSelectedCountries} leftSection={<IconMapPin size={18} />} width={148} searchable clearable />
        </Group>
        <Box hiddenFrom="sm">
          <Group grow gap="sm">
            <MultiSelectWithBadge placeholder="Industry" data={industryOptions} value={selectedIndustries} onChange={setSelectedIndustries} leftSection={<IconBuilding size={18} />} searchable clearable />
            <MultiSelectWithBadge placeholder="Country" data={countryOptions} value={selectedCountries} onChange={setSelectedCountries} leftSection={<IconMapPin size={18} />} searchable clearable />
          </Group>
        </Box>
        <Flex mt="lg" mb="lg">
          <FilterChips value={activeFilters} labelMap={filterLabelMap} onRemove={handleRemoveFilter} />
        </Flex>
        */}

        {/* Marks end of list + pagination; when it enters the strip zone, mobile promo docks in-flow below */}
        <div ref={mobileDockSentinelRef} style={{ height: 0, width: "100%" }} aria-hidden />

        {shouldShowPromoCarousel && !isLgUp && mobileDocked ? (
          <Box component="aside" mt="lg" aria-label="Featured collaborations">
            {mobilePromoBar}
          </Box>
        ) : null}
      </Container>

      {/* Mobile: fixed strip until user scrolls to end, then promo is docked in Container above */}
      {shouldShowPromoCarousel && !isLgUp && !mobileDocked ? (
        <Box
          pos="fixed"
          left={0}
          right={0}
          bottom={0}
          pb="max(0px, env(safe-area-inset-bottom, 0px))"
          style={{ zIndex: 40, pointerEvents: "none" }}
          aria-label="Featured collaborations"
        >
          {mobilePromoBar}
        </Box>
      ) : null}

      {shouldShowPromoCarousel && isLgUp ? (
        <Box
          pos="fixed"
          bottom={24}
          right={24}
          w={300}
          style={{ zIndex: 40 }}
          aria-label="Featured collaborations"
        >
          <Paper
            radius="lg"
            p="xs"
            withBorder
            shadow="md"
            bg="primary.0"
            w="100%"
            style={{ overflow: "hidden" }}
          >
            <DiscoverPromoCarousel slides={discoverPromoSlides} height={168} autoplayDelayMs={4500} />
          </Paper>
        </Box>
      ) : null}

      <PaginationSignupModal
        opened={paginationSignupModalOpened}
        onClose={() => setPaginationSignupModalOpened(false)}
        flow="pagination"
      />
    </Box>
  );
}
